import { convert } from './units.ts'

/** M24-M (docs/M24.md F, rule 11; METHODS.beaufort in agronomy.ts): the Beaufort force of a wind speed. Beaufort is a published SCALE, not a unit (docs/M24.md C):
 *  the WMO gives each force 1 to 12 its range of speeds at 10 m above open, flat ground, in m/s to one decimal (force 1 from 0.3 to 1.5, force 2 from 1.6 to 3.3, …,
 *  force 12 from 32.7). The lower bounds below are those published values; they become km/h through the unit table's twin (`convert`, m_s -> km_h: ref.convert's
 *  six places), never by hand (CLAUDE.md M9). THE SPEED IS JUDGED AT THE SCALE'S OWN RESOLUTION (M24 review): it is brought to m/s through `convert` and taken to
 *  one decimal, half up, before it is compared with the published bounds — the ranges are the scale's speeds rounded to 0.1 m/s, so 1.55 m/s reads 1.6 (force 2)
 *  and 3.35 m/s reads 3.4 (force 3), as an observer reporting to 0.1 m/s would have it; DWD gives the wind to two decimals. The forecast prints "force 3" beside a
 *  speed with its `derived` source line; nothing here is a verdict, a colour or a limit (M15's treatment sentence is never printed with it, rule 2).
 *  Fixture: fixtures/beaufort.json (beaufort.test.ts). */
export const BEAUFORT_BOUNDS_MS: readonly string[] = ['0.3', '1.6', '3.4', '5.5', '8.0', '10.8', '13.9', '17.2', '20.8', '24.5', '28.5', '32.7']
/** The same lower bounds in km/h (index 0 is force 1), as `convert` gives them. */
export const BEAUFORT_BOUNDS_KMH: readonly number[] = BEAUFORT_BOUNDS_MS.map((ms) => Number(convert(ms, 'm_s', 'km_h')))
export const BEAUFORT_MAX_FORCE = BEAUFORT_BOUNDS_MS.length

/** Tenths of a m/s of a decimal text ("3.4" -> 34, "3.350000" -> 33 with the rest 50000 in millionths of a tenth). */
const TENTHS: readonly number[] = BEAUFORT_BOUNDS_MS.map((ms) => { const [i, f = '0'] = ms.split('.'); return Number(i) * 10 + Number(f[0]) })

/** The speed in tenths of a m/s as the scale publishes it: through `convert` (six places), then to one decimal, half up. */
export function beaufortTenthsMs(kmh: number): number | null {
  if (!Number.isFinite(kmh) || kmh < 0) return null
  const ms = convert(kmh.toFixed(6), 'km_h', 'm_s')                       // "3.350000"
  const [int, frac = ''] = ms.split('.')
  const f = frac.padEnd(6, '0')
  return Number(int) * 10 + Number(f[0]) + (Number(f.slice(1)) >= 50_000 ? 1 : 0)
}

/** The Beaufort force (0 to 12) of a wind speed in km/h; `null` for a negative or unreadable speed. */
export function beaufortForce(kmh: number): number | null {
  if (Number.isFinite(kmh) && kmh >= BEAUFORT_BOUNDS_KMH[BEAUFORT_MAX_FORCE - 1]!) return BEAUFORT_MAX_FORCE   // a hurricane: no conversion needed (nor a huge number)
  const tenths = beaufortTenthsMs(kmh)
  if (tenths === null) return null
  let force = 0
  for (const bound of TENTHS) if (tenths >= bound) force++
  return force
}
